'use client';
import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import {
  FileSpreadsheet, FileText, Download, CheckCircle2, XCircle,
  Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Loader2, Package, RotateCcw, Eye, Filter, BarChart3, Tag, X,
} from 'lucide-react';
import { thesisService } from '@/lib/services/thesisService';
import { useAuth } from '@/contexts/AuthContext';

// Curated SCOPUS keyword options for the approved-phase keyword editor.
// Mirrors the list used in PendingQueue so librarians can adjust keywords
// after a thesis has been approved/published.
const ELSEVIER_KW_OPTIONS = [
  'Computer Science', 'Engineering', 'Mathematics', 'Physics', 'Biology',
  'Chemistry', 'Medicine', 'Business Administration', 'Economics', 'Finance',
  'Psychology', 'Education', 'Law', 'Political Science', 'Sociology',
  'Linguistics', 'History', 'Philosophy', 'Architecture', 'Environmental Science',
  'Data Science', 'Machine Learning', 'Artificial Intelligence', 'Software Engineering',
  'Public Health', 'Nursing', 'Pharmacy', 'Materials Science', 'Statistics',
];

export default function ReportsExport() {
  const { user } = useAuth();
  const [theses, setTheses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<'excel' | 'docx' | 'csv' | 'pdfs' | 'all' | null>(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterExport, setFilterExport] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [sortCol, setSortCol] = useState<'approved_at' | 'author_surname' | 'department'>('approved_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);

  // Approved-phase keyword editor state
  const [editingKwId, setEditingKwId] = useState<string | null>(null);
  const [editedKws, setEditedKws] = useState<Record<string, string[]>>({});
  const [kwSearch, setKwSearch] = useState('');
  const [savingKwId, setSavingKwId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [data, analytics] = await Promise.all([
        thesisService.getApprovedAndPublished(),
        thesisService.getReportAnalytics(),
      ]);
      setTheses(data);
      setStats(analytics);
    } catch (err) {
      console.log('Load reports error:', err);
    } finally {
      setLoading(false);
    }
  };

  const uniqueDepts = Array.from(new Set(theses.map(t => t.department).filter(Boolean)));

  const filtered = useMemo(() => {
    return theses.filter(t => {
      const q = search.toLowerCase();
      const matchSearch = !q || t.title_primary?.toLowerCase().includes(q) || `${t.author_surname}, ${t.author_name}`.toLowerCase().includes(q);
      const matchType = filterType === 'all' || t.doc_type === filterType;
      const matchExport = filterExport === 'all' || (filterExport === 'exported' && t.is_exported) || (filterExport === 'not_exported' && !t.is_exported);
      const matchDept = filterDept === 'all' || t.department === filterDept;
      return matchSearch && matchType && matchExport && matchDept;
    }).sort((a, b) => {
      let cmp = 0;
      if (sortCol === 'approved_at') cmp = (a.approved_at || '').localeCompare(b.approved_at || '');
      else if (sortCol === 'author_surname') cmp = (a.author_surname || '').localeCompare(b.author_surname || '');
      else if (sortCol === 'department') cmp = (a.department || '').localeCompare(b.department || '');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [theses, search, filterType, filterExport, filterDept, sortCol, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: typeof sortCol }) => {
    if (sortCol !== col) return <ChevronDown size={12} className="text-[#c0c8d8]" />;
    return sortDir === 'asc' ? <ChevronUp size={12} className="text-[#1a2e5a]" /> : <ChevronDown size={12} className="text-[#1a2e5a]" />;
  };

  const toggleRow = (id: string) => setSelectedRows(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  const toggleAll = () => setSelectedRows(selectedRows.length === paginated.length ? [] : paginated.map(r => r.id));

  // Get the theses to export (selected or all filtered)
  const getExportTargets = () => {
    if (selectedRows.length > 0) {
      return theses.filter(t => selectedRows.includes(t.id));
    }
    return filtered;
  };

  // Export to Excel
  const handleExportExcel = async () => {
    setExporting('excel');
    try {
      const { exportThesesToExcel } = await import('@/lib/export/excelExport');
      const targets = getExportTargets();
      const count = await exportThesesToExcel(targets);
      toast.success(`Exported ${count} theses to Excel.`);
    } catch (err: any) {
      toast.error(`Excel export failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setExporting(null);
    }
  };

  // Export to DOCX ZIP
  const handleExportDocxZip = async () => {
    setExporting('docx');
    try {
      const { exportThesesToDocxZip } = await import('@/lib/export/docxExport');
      const targets = getExportTargets();
      const count = await exportThesesToDocxZip(targets);
      toast.success(`Exported ${count} thesis Word documents in ZIP.`);
    } catch (err: any) {
      toast.error(`DOCX export failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setExporting(null);
    }
  };

  // Export to CSV (national-standard flat record)
  const handleExportCsv = async () => {
    setExporting('csv');
    try {
      const { exportThesesToCsv } = await import('@/lib/export/csvExport');
      const targets = getExportTargets();
      const count = await exportThesesToCsv(targets);
      toast.success(`Exported ${count} theses to CSV.`);
    } catch (err: any) {
      toast.error(`CSV export failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setExporting(null);
    }
  };

  // Export original thesis PDF documents as a ZIP
  const handleExportPdfsZip = async () => {
    setExporting('pdfs');
    try {
      const { exportThesesToPdfsZip } = await import('@/lib/export/pdfsZipExport');
      const targets = getExportTargets();
      const count = await exportThesesToPdfsZip(targets);
      toast.success(`Packaged ${count} PDF files into ZIP.`);
    } catch (err: any) {
      toast.error(`PDFs ZIP export failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setExporting(null);
    }
  };

  // Export everything — CSV + Excel + Word ZIP + PDFs ZIP — and mark as exported.
  // This is the canonical "exported" action per the national-standard workflow.
  const handleExportAllAndMark = async () => {
    setExporting('all');
    try {
      const { exportThesesToExcel } = await import('@/lib/export/excelExport');
      const { exportThesesToDocxZip } = await import('@/lib/export/docxExport');
      const { exportThesesToCsv } = await import('@/lib/export/csvExport');
      const { exportThesesToPdfsZip } = await import('@/lib/export/pdfsZipExport');
      const targets = getExportTargets();

      await exportThesesToCsv(targets);
      await exportThesesToExcel(targets);
      await exportThesesToDocxZip(targets);
      await exportThesesToPdfsZip(targets);

      const ids = targets.map((t) => t.id);
      if (user) {
        const success = await thesisService.markExported(ids, user.id);
        if (success) {
          setTheses((prev) => prev.map((t) => ids.includes(t.id) ? { ...t, is_exported: true, exported_at: new Date().toISOString() } : t));
          toast.success(`Exported ${ids.length} theses (CSV + Excel + Word ZIP + PDFs ZIP) and marked as exported.`, { duration: 6000 });
        }
      }
    } catch (err: any) {
      toast.error(`Export failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setExporting(null);
      setSelectedRows([]);
    }
  };

  // Legacy: Export Excel + DOCX ZIP + mark (kept for backwards compatibility)
  const handleExportAndMark = async () => {
    setExporting('excel');
    try {
      const { exportThesesToExcel } = await import('@/lib/export/excelExport');
      const { exportThesesToDocxZip } = await import('@/lib/export/docxExport');
      const targets = getExportTargets();

      // Export both
      await exportThesesToExcel(targets);
      setExporting('docx');
      await exportThesesToDocxZip(targets);

      // Mark as exported
      const ids = targets.map(t => t.id);
      if (user) {
        const success = await thesisService.markExported(ids, user.id);
        if (success) {
          setTheses(prev => prev.map(t => ids.includes(t.id) ? { ...t, is_exported: true, exported_at: new Date().toISOString() } : t));
          toast.success(`Exported and marked ${ids.length} theses as exported.`);
        }
      }
    } catch (err: any) {
      toast.error(`Export failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setExporting(null);
      setSelectedRows([]);
    }
  };

  // Mark selected as exported (without re-downloading)
  const handleMarkExported = async () => {
    if (selectedRows.length === 0) { toast.error('Select theses to mark.'); return; }
    if (!user) return;
    const success = await thesisService.markExported(selectedRows, user.id);
    if (success) {
      setTheses(prev => prev.map(t => selectedRows.includes(t.id) ? { ...t, is_exported: true, exported_at: new Date().toISOString() } : t));
      toast.success(`Marked ${selectedRows.length} theses as exported.`);
      setSelectedRows([]);
    } else {
      toast.error('Failed to mark theses as exported.');
    }
  };

  // Unmark selected as exported
  const handleUnmarkExported = async () => {
    if (selectedRows.length === 0) { toast.error('Select theses to unmark.'); return; }
    const success = await thesisService.unmarkExported(selectedRows);
    if (success) {
      setTheses(prev => prev.map(t => selectedRows.includes(t.id) ? { ...t, is_exported: false, exported_at: null } : t));
      toast.success(`Unmarked ${selectedRows.length} theses.`);
      setSelectedRows([]);
    } else {
      toast.error('Failed to unmark theses.');
    }
  };

  // ─── Approved-phase keyword editing ─────────────────────────────────────
  const getKeywordsFor = (id: string): string[] => {
    if (editedKws[id]) return editedKws[id];
    const t = theses.find((x) => x.id === id);
    return (t?.thesis_keywords || []).map((k: any) => k.keyword_label);
  };

  const toggleEditedKeyword = (id: string, kw: string) => {
    const current = getKeywordsFor(id);
    setEditedKws((prev) => ({
      ...prev,
      [id]: current.includes(kw) ? current.filter((k) => k !== kw) : [...current, kw],
    }));
  };

  const saveKeywordEdits = async (id: string) => {
    setSavingKwId(id);
    try {
      const kws = getKeywordsFor(id);
      const ok = await thesisService.updateThesisKeywords(id, kws);
      if (ok) {
        // Reflect the updated keyword list locally
        setTheses((prev) => prev.map((t) => t.id === id
          ? { ...t, thesis_keywords: kws.map((label) => ({ keyword_label: label, is_student_keyword: false })) }
          : t));
        setEditedKws((prev) => { const next = { ...prev }; delete next[id]; return next; });
        setEditingKwId(null);
        toast.success('Keywords updated.');
      } else {
        toast.error('Failed to update keywords.');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update keywords.');
    } finally {
      setSavingKwId(null);
    }
  };

  const cancelKeywordEdits = (id: string) => {
    setEditedKws((prev) => { const next = { ...prev }; delete next[id]; return next; });
    setEditingKwId(null);
  };

  const filteredKwOptions = ELSEVIER_KW_OPTIONS.filter((k) =>
    !kwSearch || k.toLowerCase().includes(kwSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-[#1a2e5a]" />
      </div>
    );
  }

  const exportedCount = theses.filter(t => t.is_exported).length;
  const notExportedCount = theses.length - exportedCount;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-700 text-[#0f1a35]">Reports & Export</h2>
          <p className="text-sm text-[#4a5578] mt-0.5">
            <span className="font-600 text-[#1a2e5a]">{theses.length}</span> approved/published theses
          </p>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-[#e2e6f0] rounded-xl p-4">
          <p className="text-xs text-[#8892a8] font-600 uppercase tracking-wide">Total</p>
          <p className="text-2xl font-700 text-[#0f1a35] mt-1">{theses.length}</p>
        </div>
        <div className="bg-white border border-emerald-200 rounded-xl p-4">
          <p className="text-xs text-emerald-600 font-600 uppercase tracking-wide">Exported</p>
          <p className="text-2xl font-700 text-emerald-700 mt-1">{exportedCount}</p>
        </div>
        <div className="bg-white border border-amber-200 rounded-xl p-4">
          <p className="text-xs text-amber-600 font-600 uppercase tracking-wide">Not Exported</p>
          <p className="text-2xl font-700 text-amber-700 mt-1">{notExportedCount}</p>
        </div>
        <div className="bg-white border border-purple-200 rounded-xl p-4">
          <p className="text-xs text-purple-600 font-600 uppercase tracking-wide">Departments</p>
          <p className="text-2xl font-700 text-purple-700 mt-1">{uniqueDepts.length}</p>
        </div>
      </div>

      {/* Export actions bar */}
      <div className="bg-white border border-[#e2e6f0] rounded-xl p-4 mb-5 flex flex-wrap items-center gap-3">
        <span className="text-xs font-600 text-[#4a5578] uppercase tracking-wide mr-2">
          {selectedRows.length > 0 ? `${selectedRows.length} selected` : `${filtered.length} theses in view`}
        </span>
        <div className="h-5 w-px bg-[#e2e6f0]" />
        <button onClick={handleExportCsv} disabled={!!exporting}
          className="flex items-center gap-1.5 text-xs font-600 text-[#1a2e5a] bg-[#1a2e5a]/5 hover:bg-[#1a2e5a]/10 border border-[#1a2e5a]/15 px-3 py-2 rounded-lg transition-all disabled:opacity-50">
          {exporting === 'csv' ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
          Export CSV
        </button>
        <button onClick={handleExportExcel} disabled={!!exporting}
          className="flex items-center gap-1.5 text-xs font-600 text-[#1a2e5a] bg-[#1a2e5a]/5 hover:bg-[#1a2e5a]/10 border border-[#1a2e5a]/15 px-3 py-2 rounded-lg transition-all disabled:opacity-50">
          {exporting === 'excel' ? <Loader2 size={13} className="animate-spin" /> : <FileSpreadsheet size={13} />}
          Export Excel
        </button>
        <button onClick={handleExportDocxZip} disabled={!!exporting}
          className="flex items-center gap-1.5 text-xs font-600 text-[#1a2e5a] bg-[#1a2e5a]/5 hover:bg-[#1a2e5a]/10 border border-[#1a2e5a]/15 px-3 py-2 rounded-lg transition-all disabled:opacity-50">
          {exporting === 'docx' ? <Loader2 size={13} className="animate-spin" /> : <Package size={13} />}
          Export Word ZIP
        </button>
        <button onClick={handleExportPdfsZip} disabled={!!exporting}
          className="flex items-center gap-1.5 text-xs font-600 text-[#1a2e5a] bg-[#1a2e5a]/5 hover:bg-[#1a2e5a]/10 border border-[#1a2e5a]/15 px-3 py-2 rounded-lg transition-all disabled:opacity-50">
          {exporting === 'pdfs' ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
          Export PDFs ZIP
        </button>
        <div className="h-5 w-px bg-[#e2e6f0]" />
        <button onClick={handleExportAllAndMark} disabled={!!exporting}
          className="flex items-center gap-1.5 text-xs font-600 text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-2 rounded-lg transition-all disabled:opacity-50"
          title="Exports CSV + Excel + Word ZIP + PDFs ZIP and marks selected theses as exported">
          {exporting === 'all' ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
          Export All & Mark
        </button>
        {selectedRows.length > 0 && (
          <>
            <div className="h-5 w-px bg-[#e2e6f0]" />
            <button onClick={handleMarkExported}
              className="flex items-center gap-1.5 text-xs font-600 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-2 rounded-lg transition-all">
              <CheckCircle2 size={13} /> Mark Exported
            </button>
            <button onClick={handleUnmarkExported}
              className="flex items-center gap-1.5 text-xs font-600 text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 px-3 py-2 rounded-lg transition-all">
              <RotateCcw size={13} /> Unmark Exported
            </button>
          </>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8892a8]" />
          <input type="text" placeholder="Search by title or author..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-8 pr-3 py-2 text-xs border border-[#e2e6f0] rounded-lg bg-white focus:outline-none focus:border-[#1a2e5a]" />
        </div>
        <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }}
          className="text-xs border border-[#e2e6f0] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1a2e5a]">
          <option value="all">All Types</option>
          <option value="phd">PhD</option>
          <option value="master">Master&apos;s</option>
        </select>
        <select value={filterExport} onChange={e => { setFilterExport(e.target.value); setPage(1); }}
          className="text-xs border border-[#e2e6f0] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1a2e5a]">
          <option value="all">All Export Status</option>
          <option value="exported">Exported</option>
          <option value="not_exported">Not Exported</option>
        </select>
        <select value={filterDept} onChange={e => { setFilterDept(e.target.value); setPage(1); }}
          className="text-xs border border-[#e2e6f0] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1a2e5a]">
          <option value="all">All Departments</option>
          {uniqueDepts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#e2e6f0] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#e2e6f0] bg-[#f7f8fc]">
                <th className="px-4 py-3 text-left w-8">
                  <input type="checkbox" checked={selectedRows.length === paginated.length && paginated.length > 0} onChange={toggleAll} className="accent-[#1a2e5a]" />
                </th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Title</th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide cursor-pointer" onClick={() => toggleSort('author_surname')}>
                  <div className="flex items-center gap-1">Author <SortIcon col="author_surname" /></div>
                </th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Student ID</th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide cursor-pointer" onClick={() => toggleSort('department')}>
                  <div className="flex items-center gap-1">Dept <SortIcon col="department" /></div>
                </th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Type</th>
                <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide cursor-pointer" onClick={() => toggleSort('approved_at')}>
                  <div className="flex items-center gap-1">Approved <SortIcon col="approved_at" /></div>
                </th>
                <th className="px-4 py-3 text-center font-600 text-[#4a5578] uppercase tracking-wide">Exported</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-[#8892a8]">No theses found matching your filters.</td>
                </tr>
              ) : paginated.map((t, idx) => {
                const isExpanded = expandedId === t.id;
                return (
                  <React.Fragment key={t.id}>
                    <tr className={`border-b border-[#f0f2f8] hover:bg-[#f7f8fc] transition-colors cursor-pointer ${idx % 2 === 0 ? '' : 'bg-[#fafbfd]'}`}
                      onClick={() => setExpandedId(isExpanded ? null : t.id)}>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedRows.includes(t.id)} onChange={() => toggleRow(t.id)} className="accent-[#1a2e5a]" />
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <p className="font-500 text-[#0f1a35] truncate">{t.title_primary}</p>
                        <p className="text-[#8892a8] text-xs mt-0.5 truncate">{t.thesis_keywords?.slice(0, 2).map((k: any) => k.keyword_label).join(', ')}</p>
                      </td>
                      <td className="px-4 py-3 text-[#4a5578] whitespace-nowrap">{t.author_surname}, {t.author_name}</td>
                      <td className="px-4 py-3 text-[#4a5578] font-mono text-xs">{t.student_id_number || '—'}</td>
                      <td className="px-4 py-3 text-[#4a5578] max-w-32 truncate">{t.department}</td>
                      <td className="px-4 py-3">
                        <span className={t.doc_type === 'phd' ? 'badge-phd' : 'badge-master'}>{t.doc_type === 'phd' ? 'PhD' : "Master's"}</span>
                      </td>
                      <td className="px-4 py-3 text-[#8892a8] whitespace-nowrap">{t.approved_at?.slice(0, 10) || '—'}</td>
                      <td className="px-4 py-3 text-center">
                        {t.is_exported ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-600">
                            <CheckCircle2 size={10} /> Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-[#8892a8] bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5 font-500">
                            <XCircle size={10} /> No
                          </span>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className="px-6 py-4 bg-[#f7f8fc] border-b border-[#e2e6f0]">
                          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 text-xs mb-3">
                            <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Student Email</p><p className="text-[#0f1a35]">{t.student_email || '—'}</p></div>
                            <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Registered Email</p><p className="text-[#0f1a35]">{t.student?.email || '—'}</p></div>
                            <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Supervisor</p><p className="text-[#0f1a35]">{t.supervisor_surname}, {t.supervisor_name}</p></div>
                            <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">ORCID</p><p className="text-[#0f1a35] font-mono">{t.orcid || '—'}</p></div>
                            <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">School</p><p className="text-[#0f1a35]">{t.school || '—'}</p></div>
                            <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Access Rights</p><p className="text-[#0f1a35]">{t.access_rights} · {t.license}</p></div>
                            <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Date Published</p><p className="text-[#0f1a35]">{t.date_publication || '—'}</p></div>
                            <div><p className="text-[#8892a8] font-600 uppercase tracking-wide mb-0.5">Exported At</p><p className="text-[#0f1a35]">{t.exported_at ? new Date(t.exported_at).toLocaleDateString('en-GB') : '—'}</p></div>
                          </div>
                          {t.abstract_primary && (
                            <div className="mb-2">
                              <p className="text-xs font-600 text-[#4a5578] uppercase tracking-wide mb-1">Abstract</p>
                              <p className="text-xs text-[#4a5578] leading-relaxed line-clamp-3">{t.abstract_primary}</p>
                            </div>
                          )}
                          {/* Keywords — editable by librarians at approved phase */}
                          {(() => {
                            const isEditingThis = editingKwId === t.id;
                            const currentKws = getKeywordsFor(t.id);
                            const originalKws = (t.thesis_keywords || []).map((k: any) => k.keyword_label);
                            const hasChanges = JSON.stringify([...currentKws].sort()) !== JSON.stringify([...originalKws].sort());
                            return (
                              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center justify-between mb-1.5">
                                  <p className="text-xs font-600 text-[#4a5578] uppercase tracking-wide flex items-center gap-1.5">
                                    <Tag size={11} /> Keywords
                                    {hasChanges && (
                                      <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-600 bg-amber-50 text-amber-700 border border-amber-200">
                                        Unsaved changes
                                      </span>
                                    )}
                                  </p>
                                  {!isEditingThis ? (
                                    <button onClick={() => setEditingKwId(t.id)}
                                      className="text-xs text-[#1a2e5a] hover:underline font-600">
                                      Edit keywords
                                    </button>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <button onClick={() => saveKeywordEdits(t.id)} disabled={savingKwId === t.id}
                                        className="flex items-center gap-1 text-xs font-600 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1 rounded-lg">
                                        {savingKwId === t.id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                                        Save
                                      </button>
                                      <button onClick={() => cancelKeywordEdits(t.id)}
                                        className="text-xs text-[#8892a8] hover:text-red-500 font-600">
                                        Cancel
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                  {currentKws.length === 0 ? (
                                    <span className="text-xs text-[#8892a8] italic">No keywords</span>
                                  ) : currentKws.map((kw) => (
                                    <span key={`kw-${kw}`} className="inline-flex items-center gap-1 text-xs bg-[#1a2e5a]/6 text-[#1a2e5a] border border-[#1a2e5a]/15 rounded-full px-2.5 py-0.5 font-500">
                                      {kw}
                                      {isEditingThis && (
                                        <button onClick={() => toggleEditedKeyword(t.id, kw)} className="text-[#1a2e5a]/50 hover:text-red-500 ml-0.5">
                                          <X size={10} />
                                        </button>
                                      )}
                                    </span>
                                  ))}
                                </div>
                                {isEditingThis && (
                                  <div className="border border-[#e2e6f0] rounded-xl p-3 bg-white">
                                    <div className="relative mb-2">
                                      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8892a8]" />
                                      <input type="text" placeholder="Search keywords..." value={kwSearch} onChange={(e) => setKwSearch(e.target.value)}
                                        className="w-full pl-7 pr-3 py-1.5 text-xs border border-[#e2e6f0] rounded-lg bg-white focus:outline-none focus:border-[#1a2e5a]" />
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                                      {filteredKwOptions.map((kw) => (
                                        <button key={`kopt-${kw}`} onClick={() => toggleEditedKeyword(t.id, kw)}
                                          className={`text-xs px-2.5 py-0.5 rounded-full border font-500 transition-all ${
                                            currentKws.includes(kw)
                                              ? 'bg-[#1a2e5a] text-white border-[#1a2e5a]'
                                              : 'bg-white text-[#4a5578] border-[#e2e6f0] hover:border-[#1a2e5a]'
                                          }`}>
                                          {kw}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#e2e6f0]">
            <div className="flex items-center gap-2 text-xs text-[#8892a8]">
              <span>Rows per page:</span>
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className="border border-[#e2e6f0] rounded px-2 py-1 text-xs bg-white">
                {[10, 25, 50].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <span>{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-all">
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs text-[#4a5578] px-2">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-all">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
